const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:7777/api';

// Local storage keys for BYOK (Bring Your Own Key)
const OPENROUTER_API_KEY_STORAGE_KEY = 'openrouter_api_key';
const SELECTED_MODEL_STORAGE_KEY = 'openrouter_selected_model';
const REFINEMENT_MODEL_STORAGE_KEY = 'anthropic_refinement_model';
const BROWSER_ID_STORAGE_KEY = 'mlst_browser_id';

// Browser ID service for identifying public share users
// This creates a persistent random ID stored in localStorage to isolate conversations
export const BrowserIdService = {
  // Generate a random browser ID
  generateBrowserId(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  },

  // Get or create the browser ID
  getBrowserId(): string {
    try {
      let browserId = localStorage.getItem(BROWSER_ID_STORAGE_KEY);
      if (!browserId) {
        browserId = this.generateBrowserId();
        localStorage.setItem(BROWSER_ID_STORAGE_KEY, browserId);
      }
      return browserId;
    } catch {
      // If localStorage fails, generate a session-only ID
      return this.generateBrowserId();
    }
  },

  // Clear the browser ID (for testing/debugging)
  clearBrowserId(): void {
    try {
      localStorage.removeItem(BROWSER_ID_STORAGE_KEY);
    } catch (e) {
      console.error('[BROWSER-ID] Failed to clear browser ID:', e);
    }
  }
};

// OpenRouter model information
export interface OpenRouterModel {
  id: string;
  name: string;
  contextLength: number;
  pricing: {
    prompt: number;
    completion: number;
  };
  provider: string;
  description?: string;
  supportsTools?: boolean;
  supportsStreaming?: boolean;
  maxCompletionTokens?: number | null;
}

// BYOK (Bring Your Own Key) management
export const BYOKService = {
  // Get the stored API key (returns null if not set)
  getApiKey(): string | null {
    try {
      return localStorage.getItem(OPENROUTER_API_KEY_STORAGE_KEY);
    } catch {
      return null;
    }
  },

  // Set the API key in local storage
  setApiKey(key: string): void {
    try {
      localStorage.setItem(OPENROUTER_API_KEY_STORAGE_KEY, key);
    } catch (e) {
      console.error('[BYOK] Failed to save API key:', e);
    }
  },

  // Clear the API key from local storage
  clearApiKey(): void {
    try {
      localStorage.removeItem(OPENROUTER_API_KEY_STORAGE_KEY);
    } catch (e) {
      console.error('[BYOK] Failed to clear API key:', e);
    }
  },

  // Check if an API key is stored
  hasApiKey(): boolean {
    return !!this.getApiKey();
  },

  // Get masked version of the key for display (shows first 8 and last 4 chars)
  getMaskedKey(): string | null {
    const key = this.getApiKey();
    if (!key) return null;
    if (key.length <= 16) return '****';
    return `${key.substring(0, 8)}...${key.substring(key.length - 4)}`;
  },

  // Validate API key format (basic check)
  isValidKeyFormat(key: string): boolean {
    // OpenRouter keys typically start with 'sk-or-'
    return key.startsWith('sk-or-') && key.length > 20;
  }
};

// Model persistence
export const ModelPersistence = {
  // Get the last selected generation model (OpenRouter)
  getSelectedModel(): string | null {
    try {
      return localStorage.getItem(SELECTED_MODEL_STORAGE_KEY);
    } catch {
      return null;
    }
  },

  // Save the selected generation model
  setSelectedModel(modelId: string): void {
    try {
      localStorage.setItem(SELECTED_MODEL_STORAGE_KEY, modelId);
    } catch (e) {
      console.error('[MODEL] Failed to save selected model:', e);
    }
  },

  // Get the last selected refinement model (Anthropic)
  getRefinementModel(): string | null {
    try {
      return localStorage.getItem(REFINEMENT_MODEL_STORAGE_KEY);
    } catch {
      return null;
    }
  },

  // Save the selected refinement model
  setRefinementModel(modelId: string): void {
    try {
      localStorage.setItem(REFINEMENT_MODEL_STORAGE_KEY, modelId);
    } catch (e) {
      console.error('[MODEL] Failed to save refinement model:', e);
    }
  }
};

export interface ChatRequest {
  transcript: string;
  message: string;
  model: string;
  conversationHistory?: ChatMessage[];
  reasoning?: { effort?: string; max_tokens?: number } | null;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatResponse {
  success: boolean;
  response: string;
  model: string;
  tokenUsage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export interface TTMLFile {
  id: string;
  name: string;
  content: string;
  model: string;
  generatedAt: Date;
  attempts: number;
  tokenUsage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export interface TtmlJob {
  job_id: string;
  session_id: string;
  user_email: string;
  type: 'standard' | 'best-of-3';
  model: string;
  refinement_model: string | null; // Anthropic model for refinement
  status: 'pending' | 'generating' | 'refining' | 'completed' | 'failed';
  progress: string | null;
  current_attempt: number;
  max_attempts: number;
  current_refinement: number;
  max_refinements: number;
  enable_web_access: number; // 0 or 1
  refinement_log: string | null; // JSON string
  ttml_checksum: string | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  tokenUsage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  } | null;
  cost_usd: number | null;
}

export interface RefinementJob {
  job_id: string;
  session_id: string;
  user_email: string;
  source_checksum: string;
  status: 'pending' | 'refining' | 'completed' | 'failed' | 'no_changes';
  progress: string | null;
  current_iteration: number;
  max_iterations: number;
  enable_web_access: number; // 0 or 1
  model: string;
  additional_instructions: string | null;
  blank_slate: number; // 0 or 1
  max_issues_to_fix: number;
  result_checksums: string | null; // JSON array
  no_change_iterations: string | null; // JSON array
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  total_cost_usd: number | null;
  total_tokens: string | null; // JSON with input_tokens, output_tokens
}

// Gap Analysis Types
export interface GapAnalysisSpeakerBaseline {
  speakerId: string;
  label: string;
  totalWords: number;
  totalSpeakingTime: number;
  wordsPerMinute: number;
  totalGaps: number;
  medianGap: number;
  meanGap: number;
  stdDevGap: number;
  p25Gap: number;
  p75Gap: number;
  p90Gap: number;
  iqr: number;
}

export interface GapEntry {
  speaker: number;
  gapDuration: number;
  startTime: number;
  endTime: number;
  precedingWord: string;
  followingWord: string;
  precedingConfidence: number;
  followingConfidence: number;
  zScore: number;
  percentile: number;
}

export interface GapCluster {
  startTime: number;
  endTime: number;
  duration: number;
  gapCount: number;
  avgZScore: number;
  maxZScore: number;
  avgConfidence: number;
  speakers: string[];
  severity: 'high' | 'medium' | 'low';
  editorialSignal: string;
  editorialDescription: string;
  gaps?: Array<{
    startTime: number;
    gapDuration: number;
    zScore: number;
    speaker: number | string;
    precedingWord: string;
    followingWord: string;
  }>;
}

export interface GapHistogramBin {
  binStart: number;
  binEnd: number;
  label: string;
  total: number;
  bySpeaker: Record<string, number>;
}

export interface GapAnalysisResult {
  episodeId: string;
  summary: {
    totalGaps: number;
    significantGaps: number;
    clusters: number;
    totalDuration: number;
    totalWords: number;
    thresholds: { minGapSeconds: number; relativeThreshold: number; clusterMaxGap: number };
  };
  speakerBaselines: Record<string, GapAnalysisSpeakerBaseline>;
  gaps: GapEntry[];
  clusters: GapCluster[];
  histogram: { bins: GapHistogramBin[]; maxGap: number; binWidth: number };
  windowStats: Array<{
    windowStart: number;
    windowEnd: number;
    label: string;
    totalGaps: number;
    bySpeaker: Record<string, { gapCount: number; avgDuration: number; avgZScore: number; gappinessIndex: number }>;
  }>;
  compactSummary: string;
}

export interface StoredGapAnalysis {
  available: boolean;
  summary?: GapAnalysisResult['summary'];
  speakerBaselines?: Record<string, GapAnalysisSpeakerBaseline>;
  clusters?: GapCluster[];
  histogram?: GapAnalysisResult['histogram'];
  windowStats?: GapAnalysisResult['windowStats'];
  compactSummary?: string;
  analyzedAt?: string;
}

export class OpenRouterService {
  private static async request<T>(endpoint: string, options: RequestInit = {}, token?: string): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  static async getSupportedModels(token?: string): Promise<string[]> {
    const response = await this.request<{ models: string[] }>('/supported-models', {}, token);
    return response.models;
  }

  // Fetch all available OpenRouter models (dynamic list)
  static async getOpenRouterModels(token?: string): Promise<{
    models: OpenRouterModel[];
    cached?: boolean;
    stale?: boolean;
    fallback?: boolean;
    error?: string;
  }> {
    // This endpoint doesn't require auth - it's public but rate-limited
    const url = `${API_BASE_URL}/openrouter-models`;
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      throw new Error(`Failed to fetch models: ${response.status}`);
    }

    return response.json();
  }

  static async getCachedTTMLVersions(request: { transcript: string; model: string; sessionId?: string | null }, token?: string): Promise<{ success: boolean; cachedVersions: any[]; count: number }> {
    return this.request<{ success: boolean; cachedVersions: any[]; count: number }>('/get-cached-ttml-versions', {
      method: 'POST',
      body: JSON.stringify(request),
    }, token);
  }

  static async downloadCachedTTML(checksum: string, token?: string): Promise<Blob> {
    const headers = new Headers();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(`${API_BASE_URL}/download-cached-ttml/${checksum}`, {
      method: 'GET',
      headers: headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to download cached TTML' }));
      throw new Error(errorData.error || 'Failed to download cached TTML');
    }

    return response.blob();
  }

  static async chatWithTranscript(request: ChatRequest, token?: string, userOpenRouterKey?: string | null): Promise<ChatResponse> {
    const url = `${API_BASE_URL}/chat-transcript`;
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    // Pass user's OpenRouter key in header for BYOK (Bring Your Own Key)
    if (userOpenRouterKey) {
      headers.set('X-OpenRouter-Key', userOpenRouterKey);
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  static async chatWithTranscriptStreaming(
    transcript: string,
    model: string,
    messages: Array<{ role: string; content: string | Array<any> }>,
    onChunk: (content: string, totalTokens: number) => void,
    onTtmlBlock: (ttml: string, filename: string) => void,
    onComplete: (usage?: any) => void,
    onError: (error: string) => void,
    token?: string,
    ttmlContent?: string,
    uploadedFiles?: Array<{
      id: string;
      name: string;
      type: 'text' | 'pdf' | 'image';
      content?: string; // Make content optional
      size: number;
      mimeType?: string;
    }>,
    sessionId?: string | null,
    abortController?: AbortController,
    enableTTMLOutput?: boolean,
    onToolCall?: (toolName: string, toolResult: string) => void,
    onToolError?: (toolName: string, error: string) => void,
    conversationId?: string | null,
    userOpenRouterKey?: string | null, // BYOK: User's own OpenRouter API key for public shares
    reasoning?: { effort?: string; max_tokens?: number } | null, // OpenRouter reasoning/thinking parameter
    disableTimecodeLinks?: boolean, // Disable timecode:// links in responses
    shareToken?: string | null
  ): Promise<void> {
    try {
      const headers = new Headers();
      headers.set('Content-Type', 'application/json');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      // Pass user's OpenRouter key in header for BYOK (Bring Your Own Key)
      if (userOpenRouterKey) {
        headers.set('X-OpenRouter-Key', userOpenRouterKey);
      }
      if (shareToken) {
        headers.set('X-Share-Token', shareToken);
      }

      const response = await fetch(`${API_BASE_URL}/chat-transcript-stream`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          transcript,
          model,
          messages,
          temperature: 1,
          max_tokens: 60000, // High limit to allow for reasoning tokens
          ttmlContent,
          uploadedFiles: uploadedFiles?.map(file => ({
            id: file.id,
            name: file.name,
            type: file.type,
            size: file.size,
            mimeType: file.mimeType,
            // Don't send content to reduce payload size - server will load it
          })),
          sessionId,
          conversationId,
          enableTTMLOutput,
          reasoning, // OpenRouter extended thinking parameter
          disableTimecodeLinks,
          shareToken
        }),
        signal: abortController?.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          // Check if the request was aborted
          if (abortController?.signal.aborted) {
            throw new Error('Stream cancelled by user');
          }
          
          const { done, value } = await reader.read();
          
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data.trim() === '') continue;

              try {
                const event = JSON.parse(data);
                this.handleStreamEvent(event, onChunk, onComplete, onError, onTtmlBlock, onToolCall, onToolError);
              } catch (parseError) {
                // Handle malformed JSON like: {"type":"error",...}\n\ndata: {"type":"end"}
                console.warn('[CHAT-STREAM] Primary JSON parse failed, attempting to extract multiple JSON objects');
                
                // Try to extract and parse individual JSON objects from malformed data
                this.parseMultipleJsonObjects(data, onChunk, onComplete, onError, onTtmlBlock, onToolCall, onToolError);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

    } catch (error) {
      console.error('[CHAT-STREAM] Streaming error:', error);
      onError(error instanceof Error ? error.message : 'Streaming failed');
    }
  }

  static async deleteCachedTTML(checksum: string, token?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/delete-cached-ttml/${checksum}`, {
      method: 'DELETE',
    }, token);
  }

  // ========== TTML Job Queue Methods ==========

  static async getTtmlJobs(sessionId: string, token?: string): Promise<{ success: boolean; jobs: TtmlJob[] }> {
    return this.request<{ success: boolean; jobs: TtmlJob[] }>(`/sessions/${sessionId}/ttml-jobs`, {}, token);
  }

  static async deleteTtmlJob(sessionId: string, jobId: string, token?: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/sessions/${sessionId}/ttml-jobs/${jobId}`, {
      method: 'DELETE',
    }, token);
  }

  // Get all refinement jobs for a session
  static async getRefinementJobs(sessionId: string, token?: string): Promise<{ success: boolean; jobs: RefinementJob[] }> {
    return this.request<{ success: boolean; jobs: RefinementJob[] }>(`/sessions/${sessionId}/refinement-jobs`, {}, token);
  }

  // Get a specific refinement job
  // Delete a refinement job
  static async deleteRefinementJob(sessionId: string, jobId: string, token?: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/sessions/${sessionId}/refinement-jobs/${jobId}`, {
      method: 'DELETE',
    }, token);
  }

  private static handleStreamEvent(
    event: any,
    onChunk: (content: string, totalTokens: number) => void,
    onComplete: (usage?: any) => void,
    onError: (error: string) => void,
    onTtmlBlock: (ttml: string, filename: string) => void,
    onToolCall?: (toolName: string, toolResult: string) => void,
    onToolError?: (toolName: string, error: string) => void
  ): void {
    switch (event.type) {
      case 'start':
        console.log('[CHAT-STREAM] Stream started');
        break;
      case 'content':
        console.log('[CHAT-STREAM] Content chunk received:', event.content?.length || 0, 'chars');
        onChunk(event.content, event.totalTokens);
        break;
      case 'ttml_block':
        if (event.ttml && event.filename) {
            onTtmlBlock(event.ttml, event.filename);
        }
        break;
      case 'complete':
        console.log('[CHAT-STREAM] Stream completed:', event.finish_reason);
        onComplete(event.usage);
        break;
      case 'error':
        console.log('[CHAT-STREAM] Error event received:', event.error);
        onError(event.error);
        break;
      case 'end':
        console.log('[CHAT-STREAM] End event received');
        break;
      case 'tool_call':
        console.log('[CHAT-STREAM] Tool call received:', event.tool_name, 'result length:', event.tool_result?.length || 0);
        if (onToolCall) {
          onToolCall(event.tool_name, event.tool_result);
        } else {
          console.warn('[CHAT-STREAM] No onToolCall handler provided!');
        }
        break;
      case 'tool_error':
        console.error('[CHAT-STREAM] Tool error:', event.tool_name, event.error);
        if (onToolError) {
          onToolError(event.tool_name, event.error);
        }
        break;
      case 'ping':
        // Keep-alive, ignore
        break;
      default:
        console.log('[CHAT-STREAM] Unknown event type:', event.type);
    }
  }

  private static parseMultipleJsonObjects(
    data: string,
    onChunk: (content: string, totalTokens: number) => void,
    onComplete: (usage?: any) => void,
    onError: (error: string) => void,
    onTtmlBlock: (ttml: string, filename: string) => void,
    onToolCall?: (toolName: string, toolResult: string) => void,
    onToolError?: (toolName: string, error: string) => void
  ): void {
    try {
      // Handle malformed JSON like: {"type":"error",...}\n\ndata: {"type":"end"}
      console.log('[CHAT-STREAM] Parsing malformed data:', data.substring(0, 200));
      
      // Split on the specific pattern we see in logs: \n\ndata: 
      const parts = data.split(/\\n\\ndata:\s*/);
      
      for (const part of parts) {
        if (part.trim() === '') continue;
        
        try {
          const event = JSON.parse(part);
          console.log('[CHAT-STREAM] Successfully parsed extracted JSON:', event.type);
          this.handleStreamEvent(event, onChunk, onComplete, onError, onTtmlBlock, onToolCall, onToolError);
        } catch (partParseError) {
          console.warn('[CHAT-STREAM] Failed to parse JSON part:', part.substring(0, 100), partParseError);
        }
      }
    } catch (error) {
      console.error('[CHAT-STREAM] Error in parseMultipleJsonObjects:', error);
    }
  }

  // =============================================
  // Gap Analysis Methods
  // =============================================

  static async analyzeGaps(
    sessionId: string,
    params: { minGapSeconds?: number; relativeThreshold?: number; clusterMaxGap?: number; windowSize?: number },
    token?: string
  ): Promise<GapAnalysisResult> {
    return this.request<GapAnalysisResult>(`/v1/episodes/${sessionId}/gaps/analyze`, {
      method: 'POST',
      body: JSON.stringify(params),
    }, token);
  }

  static async getStoredGapAnalysis(
    sessionId: string,
    token?: string
  ): Promise<StoredGapAnalysis> {
    return this.request<StoredGapAnalysis>(`/v1/episodes/${sessionId}/gaps`, {}, token);
  }

  static getGapTtmlDownloadUrl(sessionId: string, params: { minGapSeconds?: number; relativeThreshold?: number }): string {
    const query = new URLSearchParams();
    if (params.minGapSeconds !== undefined) query.set('minGapSeconds', String(params.minGapSeconds));
    if (params.relativeThreshold !== undefined) query.set('relativeThreshold', String(params.relativeThreshold));
    return `${API_BASE_URL}/v1/episodes/${sessionId}/gaps/ttml/download?${query.toString()}`;
  }
}

export const openRouterService = OpenRouterService;
